import React, { useEffect, useState } from 'react';
import SystemDashboard from 'layouts/SystemDashboard';
import {
  Alert,
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { extractErrorMessage } from 'utils/extractErrorMessage';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Container from 'components/Container';
import {
  getProgramme,
  saveProgramme,
  updateProgramme,
  PROGRAMME_ADDON_CODES,
} from './ProgrammeService';
import { getAllSections } from '../Sections/SectionService';

const ProgrammeDetail = (): JSX.Element => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [addonCode, setAddonCode] = useState('');
  const [status, setStatus] = useState('Active');
  const [sectionId, setSectionId] = useState('');
  const [sections, setSections] = useState<any[]>([]);
  const [errorMessage, setErrorMessage] = useState('');

  const { programmeid } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    // Load sections for the picker
    getAllSections()
      .then((response) => setSections(response.data))
      .catch((error) => setErrorMessage(extractErrorMessage(error)));

    // Load existing programme when editing
    if (programmeid) {
      getProgramme(Number(programmeid))
        .then((response) => {
          setName(response.data.name ?? '');
          setDescription(response.data.description ?? '');
          setAddonCode(response.data.addonCode ?? '');
          setStatus(response.data.status ?? 'Active');
          if (response.data.section?.id) {
            setSectionId(String(response.data.section.id));
          }
        })
        .catch((error) => setErrorMessage(extractErrorMessage(error)));
    }
  }, [programmeid]);

  function saveOrUpdate(e: React.FormEvent) {
    e.preventDefault();
    const item = { name, description, addonCode, status };
    if (programmeid) {
      updateProgramme(Number(programmeid), item)
        .then(() => navigate('/system/dashboard/programmes'))
        .catch((error) => setErrorMessage(extractErrorMessage(error)));
    } else {
      saveProgramme(Number(sectionId), item)
        .then(() => navigate('/system/dashboard/programmes'))
        .catch((error) => setErrorMessage(extractErrorMessage(error)));
    }
  }

  function cancel() {
    navigate('/system/dashboard/programmes');
  }

  const selectedMeta = PROGRAMME_ADDON_CODES.find((p) => p.code === addonCode);

  return (
    <Container>
      <SystemDashboard>
        <Box marginTop={2} marginBottom={4}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link
              underline="hover"
              color="inherit"
              href="/system/dashboard/programmes"
            >
              Programmes
            </Link>
            <Typography color="text.primary">
              {programmeid ? 'Edit Programme' : 'Add Programme'}
            </Typography>
          </Breadcrumbs>
        </Box>

        {errorMessage && (
          <Alert
            severity="error"
            onClose={() => setErrorMessage('')}
            sx={{ mb: 2 }}
          >
            {errorMessage}
          </Alert>
        )}
        <Typography variant="h5" fontWeight={700} marginBottom={4}>
          {programmeid ? 'Edit Programme' : 'Add Programme'}
        </Typography>

        <form onSubmit={saveOrUpdate}>
          <Grid container spacing={4}>
            {/* Addon Code */}
            <Grid item xs={12} sm={6}>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ marginBottom: 2 }}
              >
                Programme
              </Typography>
              <FormControl fullWidth required>
                <InputLabel id="addon-code-label">Select Programme</InputLabel>
                <Select
                  labelId="addon-code-label"
                  value={addonCode}
                  label="Select Programme"
                  onChange={(e) => {
                    setAddonCode(e.target.value);
                    const meta = PROGRAMME_ADDON_CODES.find(
                      (p) => p.code === e.target.value,
                    );
                    if (meta && !name) setName(meta.label);
                  }}
                >
                  {PROGRAMME_ADDON_CODES.map((p) => (
                    <MenuItem key={p.code} value={p.code}>
                      {p.label}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        ({p.scope})
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedMeta && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  Mode: {selectedMeta.mode} · Scope: {selectedMeta.scope}
                </Typography>
              )}
            </Grid>

            {/* Name */}
            <Grid item xs={12} sm={6}>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ marginBottom: 2 }}
              >
                Display Name
              </Typography>
              <TextField
                fullWidth
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Workout Tracker"
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ marginBottom: 2 }}
              >
                Description
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what this programme helps users achieve"
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12} sm={6}>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ marginBottom: 2 }}
              >
                Status
              </Typography>
              <FormControl fullWidth>
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  value={status}
                  label="Status"
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                  <MenuItem value="Available">Available</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Section (only needed for new programmes) */}
            {!programmeid && (
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ marginBottom: 2 }}
                >
                  Section
                </Typography>
                <FormControl fullWidth required>
                  <InputLabel id="section-label">Section</InputLabel>
                  <Select
                    labelId="section-label"
                    value={sectionId}
                    label="Section"
                    onChange={(e) => setSectionId(e.target.value)}
                  >
                    {sections.map((s: any) => (
                      <MenuItem key={s.id} value={String(s.id)}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Actions */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between">
                <Button onClick={cancel}>Cancel</Button>
                <Button type="submit" variant="contained">
                  {programmeid ? 'Update' : 'Save'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </SystemDashboard>
    </Container>
  );
};

export default ProgrammeDetail;
